/* Heat preference panel — Feb-2026 (indie-conv batch2 pt3 followup).
 *
 * Read-write control for `bookstore_users.preferred_heat_max`, shown
 * on /bookstore/account. Reuses the same PUT endpoint as the
 * first-login prompt; auto-refreshes the auth context on save so the
 * next GenrePage visit picks up the new default immediately.
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Flame, X } from 'lucide-react';
import { bookstoreApi } from '@/lib/bookstoreApi';
import { useBookstoreAuth } from '@/lib/bookstoreAuthContext';

const LEVEL_LABELS = {
  1: 'Sweet — kisses, closed door',
  2: 'Warm — flirtation, fade-to-black',
  3: 'Steamy — on-page but tasteful',
  4: 'Hot — frequent, explicit',
  5: 'Anything — surprise me',
};

export default function HeatPreferencePanel() {
  const { user, refresh } = useBookstoreAuth();
  const initial = typeof user?.preferred_heat_max === 'number'
    ? user.preferred_heat_max : 5;
  const [level, setLevel] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (typeof user?.preferred_heat_max === 'number') setLevel(user.preferred_heat_max);
  }, [user?.preferred_heat_max]);

  if (!user) return null;

  const persist = async (nextLevel) => {
    setSaving(true);
    try {
      await bookstoreApi.updatePreferences({ preferred_heat_max: nextLevel });
      toast.success(nextLevel === 5
        ? 'Filter cleared — every heat level will be shown.'
        : `Saved — shelves default to heat ${nextLevel} and below.`);
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update.');
    } finally { setSaving(false); }
  };

  const clearPref = async () => {
    setSaving(true);
    try {
      await bookstoreApi.updatePreferences({ preferred_heat_max: -1 });
      toast.success('Preference cleared. Shelves show every heat level.');
      setLevel(5);
      // Also drop the "already shown" marker so a user who clears from
      // this panel gets the friendly first-login prompt again.
      try { localStorage.removeItem('bs-heat-prompt-shown'); } catch {}
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clear.');
    } finally { setSaving(false); }
  };

  const hasPref = typeof user.preferred_heat_max === 'number';

  return (
    <div className="rounded-lg border border-border bg-card p-4 sm:p-5"
         data-testid="bs-heat-preference-panel">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        <Flame className="h-3 w-3 text-orange-500" fill="currentColor" />
        Heat preference
      </div>
      <h3 className="mt-1 text-[15px] font-semibold tracking-tight">
        Set the spice ceiling for every shelf
      </h3>
      <p className="mt-1 text-[12px] text-muted-foreground">
        Every genre shelf will pre-filter to titles at or below this
        heat level. You can still adjust each shelf individually.
      </p>

      <div className="mt-4 flex items-center gap-3">
        <input
          type="range"
          min={0}
          max={5}
          step={1}
          value={level}
          onChange={(e) => setLevel(parseInt(e.target.value, 10) || 0)}
          onMouseUp={(e) => persist(parseInt(e.target.value, 10) || 0)}
          onTouchEnd={(e) => persist(parseInt(e.target.value, 10) || 0)}
          onKeyUp={(e) => persist(parseInt(e.target.value, 10) || 0)}
          disabled={saving}
          data-testid="bs-heat-preference-slider"
          className="flex-1 accent-orange-500"
          aria-label="Maximum heat level"
        />
        <span
          className="inline-flex min-w-[7.5rem] items-center justify-center gap-1 rounded-full bg-orange-500/20 px-2.5 py-1 text-[11px] font-semibold text-orange-700 dark:text-orange-300"
          data-testid="bs-heat-preference-value"
        >
          {level === 0 ? (
            <>Sweet only</>
          ) : (
            <>
              {Array.from({ length: level }).map((_, i) => (
                <Flame key={i} className="h-3 w-3" fill="currentColor" strokeWidth={2} />
              ))}
              <span className="ml-1">/ 5</span>
            </>
          )}
        </span>
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground" data-testid="bs-heat-preference-caption">
        {LEVEL_LABELS[level] || 'No filter — every heat level.'}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            disabled={saving}
            onClick={() => { setLevel(n); persist(n); }}
            data-testid={`bs-heat-preference-preset-${n}`}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-[3px] text-[11px] font-semibold uppercase tracking-wider transition disabled:opacity-60 ${
              level === n
                ? 'border-orange-500 bg-orange-500 text-white'
                : 'border-border bg-secondary/50 text-muted-foreground hover:border-orange-500/60 hover:text-foreground'
            }`}
            aria-pressed={level === n}
          >
            {Array.from({ length: n }).map((_, i) => (
              <Flame key={i} className="h-3 w-3" fill="currentColor" strokeWidth={2} />
            ))}
          </button>
        ))}
        {hasPref && (
          <button
            type="button"
            onClick={clearPref}
            disabled={saving}
            data-testid="bs-heat-preference-clear"
            className="ml-1 inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-[3px] text-[11px] uppercase tracking-wider text-muted-foreground hover:border-primary/50 hover:text-foreground disabled:opacity-60"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>
    </div>
  );
}
